import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { MailIcon, UsersIcon, UserPlusIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataListToolbar } from '@/components/data/DataListToolbar'
import {
  PersistentListCard,
  type PersistentListCardEmptyProps,
} from '@/components/data/PersistentListCard'
import {
  getEmployeesApi,
  getProfileApi,
  type EmployeeUser,
  type EmployeesResponse,
} from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 20

/** Color treatment for each role badge. */
const ROLE_STYLES: Record<string, string> = {
  employee: 'bg-muted text-muted-foreground',
  hr_manager: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  payroll_admin: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  company_admin: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
}

/** Derive a two-letter initials avatar from a display name, handling empties. */
function initialsFor(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Format an ISO timestamp as a friendly "Joined {Month Year}" string. */
function formatJoinedDate(iso: string): string {
  try {
    const d = new Date(iso)
    return `Joined ${d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
  } catch {
    return ''
  }
}

export const Route = createFileRoute('/dashboard/employees')({
  component: () => {
    const { user } = useAuth()
    const routerState = useRouterState()
    const isListRoute = routerState.location.pathname === '/dashboard/employees'

    const [response, setResponse] = useState<EmployeesResponse | null>(null)
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [searchVersion, setSearchVersion] = useState(0)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [fetchError, setFetchError] = useState<string | null>(null)
    // Tracks which employee photos failed so we render initials instead.
    const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set())

    // employerId is read through a ref so `fetchData` can have an empty
    // dep array (and thus a stable identity). Otherwise the auth-context
    // transition `user: null → user` would re-create fetchData and trigger
    // a duplicate fetch via the [page, fetchData] dep in the effect below.
    const employerIdRef = useRef<number | null>(user?.orgId ?? user?.employerId ?? null)
    const searchRef = useRef('')
    useEffect(() => {
      employerIdRef.current = user?.orgId ?? user?.employerId ?? null
    }, [user?.orgId, user?.employerId])

    const fetchData = useCallback(
      async (
        targetPage: number,
        isRefresh: boolean,
        signal?: { cancelled: boolean },
      ) => {
        if (signal?.cancelled) return
        if (isRefresh) setRefreshing(true)
        else setLoading(true)
        setFetchError(null)
        try {
          let id = employerIdRef.current
          if (id === null || id === undefined) {
            const profile = await getProfileApi()
            if (signal?.cancelled) return
            id = profile.user.org_id ?? null
            employerIdRef.current = id
          }
          if (id === null || id === undefined) {
            if (!signal?.cancelled) {
              setLoading(false)
              setRefreshing(false)
            }
            return
          }
          const data = await getEmployeesApi(id, targetPage, PAGE_SIZE, searchRef.current)
          if (signal?.cancelled) return
          setResponse(data)
          setBrokenImages(new Set())
        } catch (err) {
          if (signal?.cancelled) return
          setFetchError(
            err instanceof Error ? err.message : 'Failed to load employees',
          )
          toast.error(err instanceof Error ? err.message : 'Failed to load employees')
        } finally {
          if (!signal?.cancelled) {
            setLoading(false)
            setRefreshing(false)
          }
        }
      },
      [],
    )

    // Sync search ref so fetchData's stable closure always reads the latest value.
    useEffect(() => {
      searchRef.current = search
    }, [search])

    // Skip initial mount so the default empty search doesn't double-fire.
    const mountedRef = useRef(false)

    // Debounce: when search changes after mount, reset page to 1 and bump
    // searchVersion to trigger a fresh API fetch via [page, searchVersion, fetchData].
    useEffect(() => {
      if (!mountedRef.current) {
        mountedRef.current = true
        return
      }
      const timer = setTimeout(() => {
        setPage(1)
        setSearchVersion((v) => v + 1)
      }, 300)
      return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
      if (!isListRoute) return
      const signal = { cancelled: false }
      fetchData(page, false, signal)
      return () => {
        signal.cancelled = true
      }
    }, [page, searchVersion, fetchData, isListRoute])

    useEffect(() => {
      if (!response) return
      const newTotalPages = Math.max(1, Math.ceil(response.total / PAGE_SIZE))
      if (page > newTotalPages) setPage(1)
    }, [response, page])

    const employees: EmployeeUser[] = response?.data ?? []
    const total = response?.total ?? 0
    const companyName = response?.org_name ?? 'your company'
    const initialLoaded = response !== null || fetchError !== null
    const isInitialLoad = loading && !initialLoaded

    const subtitle = isInitialLoad ? (
      <span className="inline-block h-4 w-48 animate-pulse rounded bg-muted align-middle" />
    ) : (
      <>
        {companyName} — <span className="font-medium text-foreground">{total}</span>{' '}
        {total === 1 ? 'employee' : 'employees'}
      </>
    )

    const empty: PersistentListCardEmptyProps | null =
      total === 0 && !search.trim()
        ? {
            icon: <UsersIcon className="size-5" />,
            title: 'No employees yet.',
            description:
              'Once employees are added to your company, they will appear here.',
          }
        : {
            icon: <UsersIcon className="size-5" />,
            title: 'No employees match your search.',
            description: 'Try a different name, email, or pseudonym.',
          }

    const infoText = search.trim() ? (
      <p className="text-xs text-muted-foreground">
        Showing {employees.length} result{employees.length !== 1 ? 's' : ''} for "{search.trim()}".
      </p>
    ) : null

    // When a child route like $userId is active, render only the outlet.
    if (!isListRoute) {
      return <Outlet />
    }

    return (
      <div className="flex flex-col gap-6">
        <DataListToolbar
          title="Employees"
          subtitle={subtitle}
          search={{
            value: search,
            onChange: setSearch,
            placeholder: 'Search by name, email, or pseudonym',
            ariaLabel: 'Search employees',
          }}
          onRefresh={() => fetchData(page, true)}
          refreshing={refreshing}
          loading={loading}
          actions={
            <Button asChild size="sm" className="gap-1.5">
              <Link to="/dashboard/add-employee">
                <UserPlusIcon className="size-4" />
                Add employee
              </Link>
            </Button>
          }
        />

        <PersistentListCard<EmployeeUser>
          loading={loading}
          initialLoaded={initialLoaded}
          error={fetchError}
          errorTitle="Couldn't load employees"
          onRetry={() => fetchData(page, true)}
          empty={empty}
          items={employees}
          itemKey={(e) => e.user_id}
          infoText={infoText}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          pageLoading={loading}
          renderItem={(employee) => {
            const showPhoto =
              employee.profile_photo_url && !brokenImages.has(employee.user_id)
            return (
              <Link
                to="/dashboard/employees/$userId"
                params={{ userId: String(employee.user_id) }}
                className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 transition-all hover:bg-muted/30 hover:shadow-md"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[var(--brand-mint)]/10 text-sm font-semibold text-[var(--brand-mint)]"
                    aria-hidden
                  >
                    {showPhoto ? (
                      <img
                        src={employee.profile_photo_url ?? ''}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                        onError={() => {
                          setBrokenImages((prev) => {
                            const next = new Set(prev)
                            next.add(employee.user_id)
                            return next
                          })
                        }}
                      />
                    ) : (
                      <span>{initialsFor(employee.name)}</span>
                    )}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {employee.name}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                          ROLE_STYLES[employee.role] ?? 'bg-muted text-muted-foreground',
                        )}
                      >
                        {employee.role.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="truncate">{employee.pseudonym}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <MailIcon className="size-3.5" />
                        {employee.email}
                      </span>
                      <span>{formatJoinedDate(employee.created_at)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            )
          }}
        />
      </div>
    )
  },
})
